import { useEffect, useState, type ReactNode } from 'react';
import { ink } from '../lib/theme';
import { IconX } from './icons';

/**
 * Drawer (PRD §4-A, nível 3): desliza da direita — deliberadamente uma entrada
 * DIFERENTE do Scale Modal, pra nunca confundir "só vou espiar" (nível 2) com
 * "vou revisar o cliente inteiro" (nível 3).
 */
export function Drawer({ open, onClose, title, subtitle, width = 520, children }: {
  open: boolean; onClose: () => void; title: string; subtitle?: ReactNode; width?: number; children: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => { if (open) { setMounted(true); setClosing(false); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); requestClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function requestClose() {
    setClosing(true);
    setTimeout(() => { setMounted(false); setClosing(false); onClose(); }, 240);
  }

  if (!open && !mounted) return null;
  const active = open && !closing;

  return (
    <div
      role="dialog" aria-modal="true" aria-label={title}
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 210, display: 'flex', justifyContent: 'flex-end',
        background: 'rgba(10,10,18,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        opacity: active ? 1 : 0, transition: 'opacity 0.22s ease',
      }}
    >
      <aside style={{
        width: Math.min(width, window.innerWidth - 40), height: '100%', display: 'flex', flexDirection: 'column',
        background: 'var(--drawer-bg)',
        backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)',
        borderLeft: `1px solid ${ink(0.12)}`, boxShadow: '-30px 0 90px rgba(8,8,16,0.6)',
        transform: active ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.28s cubic-bezier(0.22,1,0.36,1)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 24px',
          borderBottom: `1px solid ${ink(0.08)}`, flex: 'none',
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 780, letterSpacing: '-0.02em', wordBreak: 'break-word' }}>{title}</div>
            {subtitle && <div style={{ marginTop: 6 }}>{subtitle}</div>}
          </div>
          <button
            type="button" onClick={requestClose} aria-label="Fechar"
            style={{
              width: 30, height: 30, borderRadius: 10, flex: 'none', border: `1px solid ${ink(0.14)}`,
              background: ink(0.05), color: ink(0.6), cursor: 'pointer', font: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          ><IconX size={13} /></button>
        </div>
        <div style={{ padding: 24, overflow: 'auto', minHeight: 0, flex: 1 }}>{children}</div>
      </aside>
    </div>
  );
}
